<!-- Preview widget for TVTemplate or EditableTVSpotTemplate objects.

Those two models are similar enough that this widget can be used for
either based on individual props being passed instead of a single
serialized object.

-->

<template>
  <div
    style="position: relative"
    :style="{
      'max-width': previewWidth + 'px',
    }"
  >
    <!-- Animated loading spinner -->
    <v-scale-transition>
      <v-layout
        v-if="showLoadingIndicator"
        style="
          position: absolute;
          z-index: 2;
          width: 100%;
          height: 100%;
          margin-left: 0;
        "
        justify-center
        align-center
      >
        <spinner-animation
          color="#2d3033"
          :size="72"
          :thickness="5"
        />
      </v-layout>
    </v-scale-transition>
    <!-- HTML Preview iframe -->
    <div
      style="transform-origin: top left"
      :style="{
        transform: `scale(${previewScaleMultiplier})`,
        'min-width': previewWidth + 'px',
        'min-height': previewHeight + 'px',
        'max-width': previewWidth + 'px',
        'max-height': previewHeight + 'px',
      }"
    >
      <video
        v-if="backgroundVideo"
        ref="background-video"
        :src="backgroundVideo"
        style="position: absolute"
        muted
        autoplay
        loop
      />
      <iframe
        ref="iframe"
        loading="lazy"
        :src="htmlOverlay"
        :width="width"
        :height="height"
        class="preview-iframe"
        style="position: relative; border: none; pointer-events: none"
        :style="{
          background:
            !backgroundVideo && backgroundImage
              ? backgroundImageUrlString
              : 'transparent',
          opacity: iframeOpacity,
        }"
        scrolling="no"
      />
    </div>
    <!-- Extra Buttons -->
    <v-slide-y-reverse-transition>
      <div
        v-if="showZoomButtons"
        style="position: absolute; bottom: 0; right: 0; z-index: 2"
      >
        <v-btn
          small
          icon
          class="grey lighten-3 black--text mr-2 mb-2"
          :title="
            zoomed
              ? $t('tvtemplatesPreviewZoomIn')
              : $t('tvtemplatesPreviewZoomOut')
          "
          @click="zoomed = !zoomed"
        >
          <v-icon v-if="zoomed">
            zoom_out
          </v-icon>
          <v-icon v-else>
            zoom_in
          </v-icon>
        </v-btn>
      </div>
    </v-slide-y-reverse-transition>
    <!-- Player Controls -->
    <v-slide-y-transition>
      <div
        v-if="showPlayerControls"
        class="pl-2 pr-3 py-1 full-width player-controls"
      >
        <v-layout align-center>
          <v-btn
            v-if="!hideRestartButton"
            small
            icon
            class="ml-0 mr-1 my-0"
            style="z-index: 2"
            :title="$t('tvtemplatesPreviewRestart')"
            @click="onRestartButtonClicked()"
          >
            <v-icon color="white">
              replay
            </v-icon>
          </v-btn>
        </v-layout>
        <!-- Fake Progressbar -->
        <v-progress-linear
          v-if="showPlayerProgressbar"
          :key="'player_progressbar_' + playerProgressbarRenderKey"
          :value="playerCurrentFrame / (playerTotalFrames / 100.0)"
          min="0"
          :max="playerTotalFrames / 100.0"
          :color="progressbarColor"
          :height="progressbarHeight"
          :background-opacity="0"
          class="ma-0"
          style="position: absolute; left: 0; bottom: -0.5px"
        />
      </div>
    </v-slide-y-transition>
  </div>
</template>

<script>
import {mapRange} from '@/utils';

import SpinnerAnimation from '@/components/SpinnerAnimation';

export default {
  components: {
    SpinnerAnimation,
  },
  props: {
    /**
     * URL to the HTML file used for preview and rendering.
     */
    htmlOverlay: {
      type: String,
      required: true,
    },
    /**
     * Width as e.g. specified on the template's or spot's TVFormat.
     */
    width: {
      type: Number,
      required: true,
    },
    /**
     * Height as e.g. specified on the template's TVFormat.
     */
    height: {
      type: Number,
      required: true,
    },
    /**
     * Image URL to be used as the background for the HTML preview.
     *
     * Use either backgroundVideo or this, not both. If we have both,
     * video is preferred. Also none can be used, so that we completely
     * rely on the HTML content.
     */
    backgroundImage: {
      type: String,
      default: null,
    },
    /**
     * Video URL to be used as the background for the HTML preview.
     *
     * Use either backgroundImage or this, not both. If we have both,
     * video is preferred. Also none can be used, so that we completely
     * rely on the HTML content.
     */
    backgroundVideo: {
      type: String,
      default: null,
    },
    /**
     * Optional maximum width of preview iframe.
     */
    maxWidth: {
      type: Number,
      default: 800,
    },
    /**
     * Optional maximum width of preview iframe.
     */
    maxHeight: {
      type: Number,
      default() {
        return this.$vuetify.breakpoint.width < 375 ? 360 : 540;
      },
    },
    /**
     * Whether we are dealing with a multiscreen format here.
     */
    isMultiscreen: {
      type: Boolean,
      default: false,
    },
    /**
     * Display zoom-in/zoom-out buttons
     */
    showZoomButtons: {
      type: Boolean,
      default: false,
    },
    /**
     * Initial zoomed-in status. Ideally paired with showZoomButtons: true.
     */
    zoomedInitially: {
      type: Boolean,
      default: true,
    },
    /**
     * Optional callback to trigger once the iframe is fully loaded.
     *
     * Can e.g. be used to populate with some initial values.
     */
    onloadCallback: {
      type: Function,
      default: null,
    },
    /**
     * A way to manually override the implicit showPlayerControls.
     */
    hidePlayerControls: {
      type: Boolean,
      default: false,
    },
    hideRestartButton: {
      type: Boolean,
      default: false,
    },
    /**
     * The first animation-restart event can usually be ignored as
     * it may come so delayed that it would disrupt the preview and
     * we autoplay anyway.
     *
     * See: https://github.com/myshopradio/cms/issues/2580).
     *
     * We still need the event to restart as the animation wraps/ends.
     */
    ignoreInitialAnimationRestart: {
      type: Boolean,
      default: true,
    },
    /**
     * Hide iframe until it is fully loaded.
     *
     * Workaround to avoid showing intermediate html state like vue
     * placeholder attributes etc.
     */
    hiddenUntilLoaded: {
      type: Boolean,
      default: true,
    },
    /**
     * Workaround to avoid flashing initial unwanted HTML state.
     *
     * HACK:
     *   This slightly defers showing the iframe once it told us it has
     *   fully into the DOM. Using $nextTick() did still show flashes of
     *   wrong state, slightly deferring with setTimeout() seems to
     *   workaround that issue.
     */
    deferLoadedTimeout: {
      type: Number,
      default: 100,
    },
    /**
     * TVTemplate duration in seconds.
     *
     * Used for animation preview control.
     */
    duration: {
      type: [String, Number],
      default: null,
    },
    /**
     * Will animate the player progress bar based on duration only.
     *
     * Only has an effect when duration is also specified.
     */
    fakePlayerProgress: {
      type: Boolean,
      default: false,
    },
    progressbarColor: {
      type: String,
      default: 'white',
    },
    progressbarHeight: {
      type: Number,
      default: 3,
    },
    /**
     * When using ffmpeg-based fades, preview may look weird unless we fake it.
     */
    fakeFadeInFadeOut: {
      type: Boolean,
      default: false,
    },
    fadeInStart: {
      type: Number,
      default: null,
    },
    fadeInStop: {
      type: Number,
      default: null,
    },
    fadeOutStart: {
      type: Number,
      default: null,
    },
    fadeOutStop: {
      type: Number,
      default: null,
    },
    busy: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loaded: false,
      zoomed: this.zoomedInitially,
      initialAnimationRestartReceived: false,

      // Player
      playerCurrentFrame: -1,
      playerProgressbarRenderKey: 0,
      playerProgressInterval: null,
      playerTotalFrames: -1,
    };
  },
  computed: {
    showLoadingIndicator() {
      if (this.busy || (this.hiddenUntilLoaded && !this.loaded)) {
        return true;
      }
      return false;
    },
    playerCurrentSeconds() {
      const fps = 25;
      const totalSeconds = this.playerTotalFrames / fps;

      const ratio = this.playerCurrentFrame / this.playerTotalFrames;
      return totalSeconds * ratio;
    },
    iframeOpacity() {
      if (this.showLoadingIndicator) {
        return 0.5;
      }

      let opacity = 1.0;
      if (this.fakeFadeInFadeOut) {
        const faded =
          this.playerCurrentSeconds < this.fadeInStart ||
          this.playerCurrentSeconds > this.fadeOutStop;
        const fadingIn =
          this.playerCurrentSeconds >= this.fadeInStart &&
          this.playerCurrentSeconds <= this.fadeInStop;
        const fadingOut =
          this.playerCurrentSeconds >= this.fadeOutStart &&
          this.playerCurrentSeconds <= this.fadeOutStop;

        if (faded) {
          opacity = 0.0;
        } else if (fadingIn) {
          const fadeInRange = this.fadeInStop - this.fadeInStart;
          const diff =
            fadeInRange - (this.fadeInStop - this.playerCurrentSeconds);
          opacity = mapRange(0, fadeInRange, 0.0, 1.0, diff);
        } else if (fadingOut) {
          const fadeOutRange = this.fadeOutStop - this.fadeOutStart;
          const diff = this.fadeOutStop - this.playerCurrentSeconds;
          opacity = mapRange(0, fadeOutRange, 0.0, 1.0, diff);
        }
      }
      return opacity;
    },
    /**
     * Automatically show player controls when template supplied player data.
     */
    showPlayerControls() {
      return !this.hidePlayerControls;
    },
    frameDataReceived() {
      return this.playerTotalFrames !== -1;
    },
    showPlayerProgressbar() {
      return (
        !this.showLoadingIndicator &&
        this.showPlayerControls &&
        this.frameDataReceived
      );
    },
    previewDimensions() {
      if (!this.width && !this.height) {
        return {};
      }

      const fixedMaxWidth = this.maxWidth;
      const fixedMaxHeight = this.maxHeight;

      const isStretch =
        this.width === 1920 && this.height === 540 && !this.isMultiscreen;
      const isPortrait = this.height > this.width && !this.isMultiscreen;
      const isLandscape = !isPortrait;

      let calculatedWidth, calculatedHeight;

      if (isStretch) {
        calculatedWidth = fixedMaxWidth;
        calculatedHeight = (calculatedWidth / 32.0) * 9.0;
      } else if (isPortrait) {
        calculatedHeight = fixedMaxHeight;
        calculatedWidth = (calculatedHeight / 16.0) * 9.0;
      } else if (isLandscape) {
        calculatedWidth = this.$vuetify.breakpoint.width - 48;
        if (calculatedWidth > fixedMaxWidth) {
          calculatedWidth = fixedMaxWidth;
        }
        calculatedHeight = (calculatedWidth / 16.0) * 9.0;

        // Apply max height.
        if (calculatedHeight > fixedMaxHeight) {
          calculatedHeight = fixedMaxHeight;
          calculatedWidth = (calculatedHeight / 9.0) * 16.0;
        }
      }

      if (!this.zoomed) {
        const zoomFactor = this.$vuetify.breakpoint.xs ? 1.33 : 1.75;
        calculatedWidth /= zoomFactor;
        calculatedHeight /= zoomFactor;
      }

      return {
        width: calculatedWidth,
        height: calculatedHeight,
      };
    },
    previewWidth() {
      return this.previewDimensions.width;
    },
    previewHeight() {
      return this.previewDimensions.height;
    },
    previewScaleMultiplier() {
      if (!this.width && !this.height) {
        return 1;
      }
      return this.previewWidth / this.width;
    },
    backgroundImageUrlString() {
      if (!this.backgroundImage) {
        return '';
      }
      return `url('${this.backgroundImage}') no-repeat`;
    },
  },
  watch: {
    /**
     * The v-progress-linear animates its bar width: If we jump back via
     * restart then the bar does not animate fast enough to actually
     * represent the current frame, which looks weird. We detect that
     * and force recreation of the progressbar widget to fix this visual
     * quirk.
     */
    playerCurrentFrame: function (frame, previousFrame) {
      if (!this.showPlayerProgressbar) {
        return;
      }
      const wentBack = frame < previousFrame;
      if (wentBack) {
        this.$nextTick(() => {
          this.playerProgressbarRenderKey += 1;
        });
      }
    },
  },
  created() {
    this.$nextTick(() => {
      this.setupIframeEventHandlers();
    });
  },
  beforeDestroy() {
    this.cleanupIframeEventHandlers();
  },
  methods: {
    onRestartButtonClicked() {
      this.restartPlayer();
      this.$emit('restart-clicked');
    },
    /** Mean to reset zoom from a parent component. */
    resetZoom() {
      this.zoomed = true;
    },
    zoomIn() {
      this.zoomed = true;
    },
    zoomOut() {
      this.zoomed = false;
    },
    setupIframeEventHandlers() {
      const vm = this;
      const iframe = vm.$refs.iframe;

      // Fake player progress (slighly deferred) and trigger onLoad callback.
      iframe.onload = function () {
        // Setup fake player progress.
        setTimeout(() => {
          vm.loaded = true;
          if (
            Number(vm.duration) &&
            (vm.fakePlayerProgress || vm.fakeFadeInFadeOut)
          ) {
            vm.setupFakedPlayerProgressInterval();
          }
        }, vm.deferLoadedTimeout);

        // Trigger onLoad callback.
        if (vm.onloadCallback) {
          vm.onloadCallback(vm);
        }
      };
    },
    cleanupIframeEventHandlers() {
      window.removeEventListener('message', this.onWindowMessageEventReceived);
    },
    onWindowMessageEventReceived(event) {
      if (event.data == 'animation-restart') {
        if (
          this.ignoreInitialAnimationRestart &&
          !this.initialAnimationRestartReceived
        ) {
          this.initialAnimationRestartReceived = true;
          return;
        }

        this.restartPlayer();
      }
    },
    setupFakedPlayerProgressInterval() {
      const vm = this;

      const fps = 25;
      const msPerTick = 50;
      const framesPerTick = (msPerTick / 1000) * fps;

      // Animation restart delay currently is hardcoded to trigger
      // immediately. If this changes we need to adapt it here as well.
      // See:
      // https://github.com/myshopradio/html-video-templates/blob/master/_components/src/animations/animations-mixin.js#L354
      const waitForFramesToRestart = 0 * fps;

      vm.playerTotalFrames = Number(vm.duration) * fps;
      vm.playerCurrentFrame = 0;

      if (vm.playerProgressInterval) {
        clearInterval(vm.playerProgressInterval);
      }
      vm.playerProgressInterval = setInterval(() => {
        vm.playerCurrentFrame += framesPerTick;
        // Wrap around if needed.
        if (
          vm.playerCurrentFrame >
          vm.playerTotalFrames + waitForFramesToRestart
        ) {
          vm.$emit('player-frame-wrapped');
          vm.playerCurrentFrame = 0;
        }
      }, msPerTick);

      // React to animation restart messages from the MyshopradioVueComponents plugin.
      window.addEventListener('message', vm.onWindowMessageEventReceived);
    },
    updateOverlayData(overlayData) {
      // We send an explicit 'overlayData' key so the template can
      // distinguish this message e.g. from the one always send at
      // initial page load.
      const iframe = this.$refs.iframe;
      if (iframe) {
        iframe.contentWindow.postMessage({overlayData: overlayData}, '*');
      }
    },
    updatePlayerFrame(frame, restart = false) {
      const payload = {
        player: {
          frame: frame,
          restart: restart,
        },
      };

      const iframe = this.$refs.iframe;
      if (iframe) {
        iframe.contentWindow.postMessage(payload, '*');
      }

      const backgroundVideo = this.$refs['background-video'];
      if (backgroundVideo) {
        backgroundVideo.pause();
        backgroundVideo.currentTime = 0;
        backgroundVideo.load();
      }
    },
    restartPlayer() {
      this.playerCurrentFrame = 0;
      const restart = true;
      this.updatePlayerFrame(this.playerCurrentFrame, restart);
    },
  },
};
</script>

<style scoped>
.preview-iframe {
  background-color: white;
}

.player-controls {
  position: absolute;
  bottom: 0px;
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.15) 0%,
    rgba(0, 0, 0, 0) 100%
  );
}
</style>



// WEBPACK FOOTER //
// src/components/TvTemplatePreview.vue